import { useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Body1, Button, Card, Field, Input, Subtitle2 } from '@fluentui/react-components';
import { Info20Filled, Warning20Filled } from '@fluentui/react-icons';
import { ButtonLoading, IconLabel } from '@shared/components';
import { ControlledField } from '@shared/components/ControlledField';
import { useLeaveGuard } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { SignInContext } from '@modules/users';
import { AccessType } from '@modules/access/types';
import { useUpdateRegistration } from '../api';
import {
  MAX_REGISTRATION_METADATA_TEXT_LENGTH,
  PAST_VALIDITY_DATE_MESSAGE,
  REGISTRATION_STATUS_OPTIONS,
} from '../constants';
import { buildRegistrationUpdate, toRegistrationFormValues } from '../helpers/registrationForm';
import { REGISTRATION_FIELDS } from '../registrationFields.config';
import { ERegistrationWarning, TRegistrationDateKey, TRegistrationDetails, TRegistrationFormValues } from '../types';
import RegistrationStatusBadge from './RegistrationStatusBadge';
import RegistrationStatusDropdown from './RegistrationStatusDropdown';

type TProps = {
  details: TRegistrationDetails;
};

const DATE_FIELDS: { key: TRegistrationDateKey; label: string }[] = [
  { key: 'validFromDate', label: 'Valid from' },
  { key: 'validUntilDate', label: 'Valid until' },
  { key: 'reminderDate', label: 'Reminder date' },
];

const getReadOnlyMessage = (details: TRegistrationDetails, hasFullLicense: boolean): string => {
  if (details.isFrozen)
    return `This ${TERMINOLOGY.registrationSingle.lower} is ${TERMINOLOGY.frozen.lower}, so its details cannot be edited.`;
  if (!hasFullLicense) return 'Editing these details requires a full license.';
  return 'You can view these details but cannot edit them.';
};

const RegistrationDetailsForm = ({ details }: TProps) => {
  const { user } = useContext(SignInContext);
  const updateRegistration = useUpdateRegistration();

  const hasFullLicense = user.license === 'FULL';
  // The backend caps a frozen folder's access at READ.
  const canEdit = !details.isFrozen && details.accessRight >= AccessType.EDIT && hasFullLicense;
  const isSaving = updateRegistration.isPending;
  const isDisabled = !canEdit || isSaving;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<TRegistrationFormValues>({ defaultValues: toRegistrationFormValues(details) });

  // A refetch (window focus, another user's save, automatic expiration) refreshes the untouched fields and keeps the
  // user's edits - unless editing is no longer allowed, which discards them. `keepDirty` stops react-hook-form from
  // briefly reporting the kept edits as clean, which would flicker Save and the leave guard.
  useEffect(() => {
    reset(toRegistrationFormValues(details), { keepDirtyValues: canEdit, keepDirty: canEdit });
  }, [details, canEdit]);

  useLeaveGuard(isDirty, () => undefined);

  const onSubmit = (values: TRegistrationFormValues) => {
    if (isDisabled) return;

    const update = buildRegistrationUpdate(details, values);
    if (!Object.keys(update).length) {
      reset(toRegistrationFormValues(details));
      return;
    }

    updateRegistration.mutate(
      { folderId: details.registrationFolder, update },
      { onSuccess: (saved) => reset(toRegistrationFormValues(saved)) }
    );
  };

  const products = details.products.map(({ name }) => name).join(', ');

  return (
    <Card className="!rounded-xl !p-5">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-wrap items-center gap-3">
          <Subtitle2>{TERMINOLOGY.registrationSingle.upper} details</Subtitle2>
          <RegistrationStatusBadge status={details.status} />
        </div>

        {details.warnings.includes(ERegistrationWarning.PAST_VALIDITY_DATE) && (
          <IconLabel icon={<Warning20Filled />}>{PAST_VALIDITY_DATE_MESSAGE}</IconLabel>
        )}

        <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 xl:grid-cols-4">
          <Field label={{ children: TERMINOLOGY.registrationCountry.upper, className: '!font-semibold' }}>
            <Body1>{details.country?.name ?? '–'}</Body1>
          </Field>
          <Field label={{ children: 'Products', className: '!font-semibold' }} className="md:col-span-1 xl:col-span-3">
            <Body1>{products || '–'}</Body1>
          </Field>

          <Field label="Status">
            <Controller
              name="status"
              control={control}
              render={({ field }) =>
                canEdit ? (
                  <RegistrationStatusDropdown
                    value={field.value}
                    options={REGISTRATION_STATUS_OPTIONS}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={isSaving}
                  />
                ) : (
                  <div className="flex min-h-8 items-center">
                    <RegistrationStatusBadge status={field.value} />
                  </div>
                )
              }
            />
          </Field>
          {DATE_FIELDS.map(({ key, label }) =>
            canEdit ? (
              <ControlledField
                key={key}
                name={key}
                label={label}
                control={control}
                errors={errors}
                inputType="date"
                valueFormat="day"
                placeholder="YYYY-MM-DD"
                clearable
                disabled={isSaving}
              />
            ) : (
              <Field key={key} label={label}>
                <Controller
                  name={key}
                  control={control}
                  render={({ field }) => <Body1 className="py-1.5">{field.value || '–'}</Body1>}
                />
              </Field>
            )
          )}

          {REGISTRATION_FIELDS.map(({ key, label }) => (
            <Field key={key} label={label}>
              <Controller
                name={`metadata.${key}`}
                control={control}
                render={({ field }) =>
                  canEdit ? (
                    <Input {...field} maxLength={MAX_REGISTRATION_METADATA_TEXT_LENGTH} disabled={isSaving} />
                  ) : (
                    <Body1 className="break-words whitespace-pre-wrap py-1.5">{field.value || '–'}</Body1>
                  )
                }
              />
            </Field>
          ))}
        </div>

        {canEdit ? (
          <div className="flex justify-end gap-2">
            <Button disabled={!isDirty || isSaving} onClick={() => reset(toRegistrationFormValues(details))}>
              Discard changes
            </Button>
            <ButtonLoading type="submit" appearance="primary" disabled={!isDirty} loading={isSaving}>
              Save
            </ButtonLoading>
          </div>
        ) : (
          <IconLabel icon={<Info20Filled />}>{getReadOnlyMessage(details, hasFullLicense)}</IconLabel>
        )}
      </form>
    </Card>
  );
};

export default RegistrationDetailsForm;
